// ApiIntegrations.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { toast } from 'react-toastify';
import { cleanString } from '../apiUtils';
import { resolveTypeRows } from './utils/integrationUtils';

// Hooks
import useIncomingIntegrations from './hooks/useIncomingIntegrations';
import useOutboundIntegrations from './hooks/useOutboundIntegrations';

// CSS
import './ApiIntegrations.css';

// Components
import Divider from 'components/divider/Divider';
import IncomingCreateWebhookModal from './components/modals/IncomingCreateWebhookModal';
import IncomingHooksSection from './components/IncomingHooksSection';
import IncomingWebhookCredentialsModal from './components/modals/IncomingWebhookCredentialsModal';
import IncomingWebhookSetupModal from './components/modals/IncomingWebhookSetupModal';
import IntegrationDeleteConfirms from './components/IntegrationDeleteConfirms';
import OutboundCreateRuleModal from './components/modals/OutboundCreateRuleModal';
import OutboundEditRuleModal from './components/modals/OutboundEditRuleModal';
import OutboundRulesSection from './components/OutboundRulesSection';

/**
 * ApiIntegrations
 *
 * Renders and manages incoming webhook and outbound rule integrations for a workspace.
 *
 * @param {string} workspaceId - The active workspace identifier.
 * @param {string} workspaceRole - The current user's role in the active workspace.
 * @param {array} schemaTypes - The schema types available to the workspace.
 *
 * @returns {ReactNode} The rendered integrations management page.
 */
const ApiIntegrations = ({ workspaceId, workspaceRole, schemaTypes }) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();


    // ----- STATE ----- //

    // Error Message State
    const [errorMessage, setErrorMessage] = useState('');

    // Status Message State
    const [statusMessage, setStatusMessage] = useState('');

    // Busy Action Key State
    const [busyActionKey, setBusyActionKey] = useState('');


    // ----- MEMOS ----- //

    // Memoized normalized schema type rows.
    const typeRows = useMemo(() => resolveTypeRows(schemaTypes), [schemaTypes]);

    // Memoized type label map by lowercased type id.
    const typeLabelMap = useMemo(() => {
        const map = new Map();
        typeRows.forEach((row) => {
            map.set(cleanString(row.typeId).toLowerCase(), row.label);
        });
        return map;
    }, [typeRows]);


    // ----- UTILITIES ----- //

    const incomingSecretHeaderName = 'X-Orbital-Incoming-Secret';
    const canManageCredentials = workspaceRole === 'owner' || workspaceRole === 'admin';


    // ----- HOOKS ----- //

    // Incoming integrations state and handlers.
    const incoming = useIncomingIntegrations({
        workspaceId,
        canManageCredentials,
        typeRows,
        incomingSecretHeaderName,
        setErrorMessage,
        setStatusMessage,
        setBusyActionKey,
    });

    // Outbound integrations state and handlers.
    const outbound = useOutboundIntegrations({
        workspaceId,
        canManageCredentials,
        typeRows,
        setErrorMessage,
        setStatusMessage,
        setBusyActionKey,
    });


    // ----- HANDLERS ----- //

    /**
     * Copies text to the clipboard and shows a success toast when possible.
     *
     * @param {string} value - The source value to copy.
     * @param {string} successMessage - Optional success toast message.
     *
     * @returns {Promise<void>} Resolves when copy logic completes.
     */
    const copyToClipboard = useCallback(async (value, successMessage) => {
        const text = cleanString(value);
        if (!text) return;

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                toast.success(successMessage || 'Copied.');
                return;
            }
        } catch (_error) {
            // fall through to prompt fallback
        }

        window.prompt('Copy this value', text);
    }, []);


    // ----- RENDER ----- //

    if (!workspaceId) {
        return (
            <div
                className="api-integrations-note"
                style={{
                    color: theme.foregroundMuted,
                }}
            >
                Select an active workspace to manage integrations.
            </div>
        );
    }

    return (
        <div className="api-integrations-container">
            {!canManageCredentials && (
                <div
                    className="api-integrations-note"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Read-only mode for this user. Owners and admins can manage integration rules.
                </div>
            )}

            {errorMessage && (
                <div className="api-integrations-error">{errorMessage}</div>
            )}

            {statusMessage && !errorMessage && (
                <div className="api-integrations-status">{statusMessage}</div>
            )}

            <IncomingHooksSection
                theme={theme}
                canManageCredentials={canManageCredentials}
                incomingLoading={incoming.incomingLoading}
                sortedIncomingHooks={incoming.sortedIncomingHooks}
                typeLabelMap={typeLabelMap}
                busyActionKey={busyActionKey}
                onAddWebhook={incoming.handleOpenIncomingCreateModal}
                onCopyUrl={copyToClipboard}
                onOpenSetup={incoming.handleOpenIncomingSetupModal}
                onToggleStatus={incoming.handleToggleIncomingHookStatus}
                onDeletePrompt={incoming.handleIncomingDeletePrompt}
            />

            <Divider />

            <OutboundRulesSection
                theme={theme}
                canManageCredentials={canManageCredentials}
                loading={outbound.loading}
                sortedRules={outbound.sortedRules}
                typeLabelMap={typeLabelMap}
                busyActionKey={busyActionKey}
                onAddRule={outbound.handleOpenCreateModal}
                onEditRule={outbound.handleOpenEditModal}
                onToggleRuleStatus={outbound.handleToggleRuleStatus}
                onTestRule={outbound.handleTestRule}
                onDeleteRule={outbound.handleDeletePrompt}
            />

            <IncomingCreateWebhookModal
                theme={theme}
                isOpen={incoming.incomingCreateModalOpen}
                onClose={incoming.handleIncomingCreateModalClose}
                canManageCredentials={canManageCredentials}
                busyActionKey={busyActionKey}
                incomingDraftName={incoming.incomingDraftName}
                incomingDraftAction={incoming.incomingDraftAction}
                incomingDraftTypeId={incoming.incomingDraftTypeId}
                typeRows={typeRows}
                onDraftNameChange={incoming.setIncomingDraftName}
                onDraftActionChange={incoming.setIncomingDraftAction}
                onDraftTypeIdChange={incoming.setIncomingDraftTypeId}
                onCreate={incoming.handleCreateIncomingHook}
            />

            <IncomingWebhookCredentialsModal
                theme={theme}
                isOpen={incoming.incomingRevealModalOpen}
                onClose={incoming.handleIncomingRevealModalClose}
                createdIncomingHookUrl={incoming.createdIncomingHookUrl}
                createdIncomingHookSecret={incoming.createdIncomingHookSecret}
                createdIncomingHeaderPreview={incoming.createdIncomingHeaderPreview}
                createdIncomingFieldRows={incoming.createdIncomingFieldRows}
                incomingSecretHeaderName={incomingSecretHeaderName}
                onCopyToClipboard={copyToClipboard}
            />

            <IncomingWebhookSetupModal
                theme={theme}
                isOpen={incoming.incomingSetupModalOpen}
                onClose={incoming.handleIncomingSetupModalClose}
                selectedIncomingHookUrl={incoming.selectedIncomingHookUrl}
                selectedIncomingHeaderPreview={incoming.selectedIncomingHeaderPreview}
                selectedIncomingFieldRows={incoming.selectedIncomingFieldRows}
                incomingSecretHeaderName={incomingSecretHeaderName}
                onCopyToClipboard={copyToClipboard}
            />

            <OutboundCreateRuleModal
                theme={theme}
                isOpen={outbound.createModalOpen}
                onClose={outbound.handleCreateModalClose}
                canManageCredentials={canManageCredentials}
                busyActionKey={busyActionKey}
                draftName={outbound.draftName}
                draftEvent={outbound.draftEvent}
                draftTypeId={outbound.draftTypeId}
                draftDestinationUrl={outbound.draftDestinationUrl}
                draftDestinationName={outbound.draftDestinationName}
                typeRows={typeRows}
                onDraftNameChange={outbound.setDraftName}
                onDraftEventChange={outbound.setDraftEvent}
                onDraftTypeIdChange={outbound.setDraftTypeId}
                onDraftDestinationUrlChange={outbound.setDraftDestinationUrl}
                onDraftDestinationNameChange={outbound.setDraftDestinationName}
                onCreateRule={outbound.handleCreateRule}
            />

            <OutboundEditRuleModal
                theme={theme}
                isOpen={outbound.editModalOpen}
                onClose={outbound.handleEditModalClose}
                canManageCredentials={canManageCredentials}
                busyActionKey={busyActionKey}
                editingRule={outbound.editingRule}
                editDraftName={outbound.editDraftName}
                editDraftEvent={outbound.editDraftEvent}
                editDraftTypeId={outbound.editDraftTypeId}
                editDraftStatus={outbound.editDraftStatus}
                editDraftDestinationUrl={outbound.editDraftDestinationUrl}
                editDraftDestinationName={outbound.editDraftDestinationName}
                typeRows={typeRows}
                onEditDraftNameChange={outbound.setEditDraftName}
                onEditDraftEventChange={outbound.setEditDraftEvent}
                onEditDraftTypeIdChange={outbound.setEditDraftTypeId}
                onEditDraftStatusChange={outbound.setEditDraftStatus}
                onEditDraftDestinationUrlChange={outbound.setEditDraftDestinationUrl}
                onEditDraftDestinationNameChange={outbound.setEditDraftDestinationName}
                onSave={outbound.handleSaveEditRule}
            />

            <IntegrationDeleteConfirms
                deleteConfirmMessage={outbound.deleteConfirmMessage}
                deleteConfirmOpen={outbound.deleteConfirmOpen}
                onDeleteConfirmOpen={outbound.handleDeleteConfirmOpen}
                onDeleteRule={outbound.handleDeleteRule}
                incomingDeleteConfirmMessage={incoming.incomingDeleteConfirmMessage}
                incomingDeleteConfirmOpen={incoming.incomingDeleteConfirmOpen}
                onIncomingDeleteConfirmOpen={incoming.handleIncomingDeleteConfirmOpen}
                onDeleteIncomingHook={incoming.handleDeleteIncomingHook}
            />
        </div>
    );
};

// Dev Tools
ApiIntegrations.displayName = 'ApiIntegrations';

export default ApiIntegrations;
